import { useState } from 'react';
import {
  App as AntApp,
  Button,
  Card,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, RedoOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  useActivatePricingPlan,
  useCreatePricingPlan,
  useDeactivatePricingPlan,
  useProjectPricingPlans,
  useUpdatePricingPlan,
  type CreatePricingPlanInput,
  type Frequency,
  type PricingPlan,
  type SurchargeMode,
} from '../api/pricing-plans';
import { formatUzs } from '@/shared/lib/format';
import { asApiError } from '@/shared/lib/api';

const FREQUENCIES: Frequency[] = ['monthly', 'quarterly'];
const SURCHARGE_MODES: SurchargeMode[] = ['flat', 'annual_financed'];

type FormValues = {
  name: string;
  termMonths: number;
  downPaymentPercent: number;
  surchargePerSqmUzs: number; // raw UZS — converted to tiyin on submit
  surchargeMode: SurchargeMode;
  frequency: Frequency;
  position?: number;
};

export function PricingPlansTab({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [includeInactive, setIncludeInactive] = useState(false);
  const { data, isLoading } = useProjectPricingPlans(projectId, includeInactive);
  const [editing, setEditing] = useState<PricingPlan | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const create = useCreatePricingPlan(projectId);
  const update = useUpdatePricingPlan(projectId);
  const deactivate = useDeactivatePricingPlan(projectId);
  const activate = useActivatePricingPlan(projectId);

  function openCreate() {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      name: '',
      termMonths: 12,
      downPaymentPercent: 30,
      surchargePerSqmUzs: 0,
      surchargeMode: 'flat',
      frequency: 'monthly',
      position: 0,
    });
    setOpen(true);
  }

  function openEdit(p: PricingPlan) {
    setEditing(p);
    form.setFieldsValue({
      name: p.name,
      termMonths: p.termMonths,
      downPaymentPercent: Number(p.downPaymentPercent),
      surchargePerSqmUzs: Number(BigInt(p.surchargePerSqm) / 100n),
      surchargeMode: p.surchargeMode,
      frequency: p.frequency,
      position: p.position,
    });
    setOpen(true);
  }

  async function onOk() {
    try {
      const v = await form.validateFields();
      const isCash = Number(v.termMonths) === 0;
      // Cash plans must be flat with no surcharge (backend invariant).
      const surchargeTiyin = isCash
        ? '0'
        : String(BigInt(Math.round(Number(v.surchargePerSqmUzs) * 100)));
      const input: CreatePricingPlanInput = {
        name: v.name,
        termMonths: Number(v.termMonths),
        downPaymentPercent: Number(v.downPaymentPercent),
        surchargePerSqm: surchargeTiyin,
        surchargeMode: isCash ? 'flat' : v.surchargeMode,
        frequency: v.frequency,
        position: Number(v.position ?? 0),
      };
      if (editing) {
        await update.mutateAsync({ id: editing.id, input });
        void message.success(t('pricingPlans.updated'));
      } else {
        await create.mutateAsync(input);
        void message.success(t('pricingPlans.created'));
      }
      setOpen(false);
      form.resetFields();
    } catch (err) {
      if ((err as { errorFields?: unknown[] }).errorFields) return;
      void message.error(asApiError(err).message);
    }
  }

  async function handleDeactivate(id: string) {
    try {
      await deactivate.mutateAsync(id);
      void message.success(t('pricingPlans.deactivated'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  async function handleActivate(id: string) {
    try {
      await activate.mutateAsync(id);
      void message.success(t('pricingPlans.activated'));
    } catch (err) {
      void message.error(asApiError(err).message);
    }
  }

  // Watch BEFORE any early return so hook order is stable.
  const termMonthsLive = Form.useWatch('termMonths', form);
  const surchargeModeLive = Form.useWatch('surchargeMode', form) as SurchargeMode | undefined;
  const isCashForm = termMonthsLive === 0;
  const isAnnualForm = surchargeModeLive === 'annual_financed';

  if (isLoading) return <Skeleton active />;

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Typography.Paragraph type="secondary" style={{ margin: 0, fontSize: 13 }}>
          {t('pricingPlans.intro')}
        </Typography.Paragraph>
        <Space>
          <Switch
            checked={includeInactive}
            onChange={setIncludeInactive}
            checkedChildren={t('pricingPlans.showInactive')}
            unCheckedChildren={t('pricingPlans.showActive')}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {t('pricingPlans.new')}
          </Button>
        </Space>
      </Space>

      <Card bodyStyle={{ padding: 0 }}>
        {(data ?? []).length === 0 ? (
          <Empty style={{ padding: 32 }} description={t('pricingPlans.empty')} />
        ) : (
          <Table<PricingPlan>
            rowKey="id"
            dataSource={data ?? []}
            pagination={false}
            size="small"
            columns={[
              {
                title: t('pricingPlans.name'),
                dataIndex: 'name',
                render: (v: string, r) => (
                  <Space size={6}>
                    <Typography.Text strong={r.active}>{v}</Typography.Text>
                    {!r.active && <Tag>{t('pricingPlans.inactive')}</Tag>}
                  </Space>
                ),
              },
              {
                title: t('pricingPlans.termMonths'),
                dataIndex: 'termMonths',
                width: 110,
                render: (v: number) => (v === 0 ? t('pricingPlans.cash') : `${v} ${t('pricingPlans.monthsAbbr')}`),
              },
              {
                title: t('pricingPlans.frequency'),
                dataIndex: 'frequency',
                width: 110,
                render: (v: Frequency) => t(`pricingPlans.frequencies.${v}`),
              },
              {
                title: t('pricingPlans.downPaymentPercent'),
                dataIndex: 'downPaymentPercent',
                width: 100,
                render: (v: string) => `${Number(v)}%`,
              },
              {
                title: t('pricingPlans.surchargePerSqm'),
                dataIndex: 'surchargePerSqm',
                width: 200,
                render: (v: string, r) =>
                  `${formatUzs(v)}${r.surchargeMode === 'annual_financed' ? '/м²·год' : '/м²'}`,
              },
              {
                title: t('pricingPlans.surchargeMode'),
                dataIndex: 'surchargeMode',
                width: 130,
                render: (v: SurchargeMode) => t(`pricingPlans.surchargeModes.${v}`),
              },
              {
                title: '',
                width: 130,
                render: (_, r) => (
                  <Space size={2}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    {r.active ? (
                      <Popconfirm
                        title={t('pricingPlans.deactivateConfirm')}
                        onConfirm={() => handleDeactivate(r.id)}
                        okText={t('common.delete')}
                        cancelText={t('common.cancel')}
                        okButtonProps={{ danger: true }}
                      >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                      </Popconfirm>
                    ) : (
                      <Button size="small" icon={<RedoOutlined />} onClick={() => handleActivate(r.id)} />
                    )}
                  </Space>
                ),
              },
            ]}
          />
        )}
      </Card>

      <Modal
        title={editing ? t('pricingPlans.editTitle') : t('pricingPlans.newTitle')}
        open={open}
        onOk={onOk}
        onCancel={() => setOpen(false)}
        confirmLoading={create.isPending || update.isPending}
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        width={620}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="name" label={t('pricingPlans.name')} rules={[{ required: true }]}>
            <Input placeholder="3 yil, 30% boshlang'ich" />
          </Form.Item>
          <Row gutter={12}>
            <Col xs={12} md={8}>
              <Form.Item
                name="termMonths"
                label={t('pricingPlans.termMonths')}
                rules={[{ required: true }]}
                extra={t('pricingPlans.termMonthsHint')}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={360} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item
                name="downPaymentPercent"
                label={t('pricingPlans.downPaymentPercent')}
                rules={[{ required: true }]}
              >
                <InputNumber<number>
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={1}
                  disabled={isCashForm}
                  addonAfter="%"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="frequency" label={t('pricingPlans.frequency')} rules={[{ required: true }]}>
                <Select
                  options={FREQUENCIES.map((f) => ({ value: f, label: t(`pricingPlans.frequencies.${f}`) }))}
                  disabled={isCashForm}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="surchargeMode"
            label={t('pricingPlans.surchargeMode')}
            rules={[{ required: true }]}
            extra={t('pricingPlans.surchargeModeHint')}
          >
            <Select
              disabled={isCashForm}
              options={SURCHARGE_MODES.map((m) => ({
                value: m,
                label: t(`pricingPlans.surchargeModes.${m}`),
              }))}
            />
          </Form.Item>
          <Form.Item
            name="surchargePerSqmUzs"
            label={isAnnualForm ? t('pricingPlans.surchargePerSqmYear') : t('pricingPlans.surchargePerSqm')}
            rules={[{ required: true }]}
            extra={isAnnualForm ? t('pricingPlans.surchargeAnnualHint') : t('pricingPlans.surchargeHint')}
          >
            <InputNumber<number>
              style={{ width: '100%' }}
              min={0}
              step={100000}
              disabled={isCashForm}
              formatter={(v) => `${v ?? ''}`.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')}
              parser={(v) => Number((v ?? '').replace(/\s/g, '')) as number}
              addonAfter={isAnnualForm ? 'UZS/м²·год' : 'UZS/м²'}
            />
          </Form.Item>
          {isCashForm && (
            <Typography.Paragraph type="warning" style={{ fontSize: 12, marginBottom: 0 }}>
              {t('pricingPlans.cashHint')}
            </Typography.Paragraph>
          )}
        </Form>
      </Modal>
    </Space>
  );
}
